import { useTranslation } from "react-i18next";
import { Trash2, AlertTriangle } from "lucide-react";
import { StatusBadge } from "../Dashboard";
import { PriorityPill, QuoteSummaryBadge, MessagesBadge, ShowInvoiceButton } from "./components";
import { displayRole } from "./constants";

// One status group of the Tasks list — header + mobile card stack + desktop
// table. Extracted from Maintenance.jsx to keep the page component lean.
// `onOpen(job, tab)` opens the detail modal on the given tab.
// Amber warning: the supplier's work-start date lands AFTER the crew's
// deadline. Only meaningful while the job isn't finished/cancelled.
export function startsAfterDeadline(it) {
  const sd = (it.start_date || "").slice(0, 10);
  const dd = (it.due_date || "").slice(0, 10);
  return !!(sd && dd && sd > dd
    && !it.deleted_at
    && !["completed", "done", "confirmed", "cancelled"].includes(it.status));
}

function LateStartChip({ it }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap"
      title={`Work starts ${(it.start_date || "").slice(0, 10)} — after your deadline ${(it.due_date || "").slice(0, 10)}`}
      data-testid={`late-start-chip-${it.id}`}
    >
      <AlertTriangle className="h-3 w-3" />Starts after deadline
    </span>
  );
}

export function TaskGroup({ group: g, members, onOpen }) {
  const { t } = useTranslation();
  const catsOf = (it) => ((it.system_categories && it.system_categories.length)
    ? it.system_categories
    : (it.system_category ? [it.system_category] : [])).slice().sort((a, b) => a.localeCompare(b));
  return (
    <div className={g.status ? "mb-6" : ""} data-testid={g.isDeleted ? "task-group-deleted" : (g.status ? `task-group-${g.status}` : undefined)}>
      {g.isDeleted && (
        <div className="flex items-center gap-2 mb-2 mt-4 pb-1.5 border-b border-zinc-200">
          <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 text-zinc-600 text-[11px] px-2 py-0.5 uppercase tracking-wider">
            <Trash2 className="h-3 w-3" />Deleted
          </span>
          <span className="text-[11px] text-zinc-500">
            {g.items.length} archived
          </span>
        </div>
      )}
      {g.status && !g.isDeleted && (
        <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-zinc-200">
          <StatusBadge value={g.status} />
          <span className="text-[11px] text-zinc-500">
            {t("tasks.count", { count: g.items.length })}
          </span>
        </div>
      )}

      {/* Invoice-style card list — one layout for all screen sizes, matching
          the Invoices page look & feel. */}
      <div className="space-y-2" data-testid="maint-cards">
        {g.items.map((it) => {
          const assignee = members.find((m) => (m.user_id || m.id) === it.assigned_to);
          // Whole card opens the Detail modal. Tasks still waiting on
          // supplier quotes jump straight to the Quotes tab with every
          // conversation expanded so the owner can reply immediately.
          const awaitingQuotes = ["open", "waiting_approval"].includes(it.status) && (it.quote_summary?.total || 0) > 0 && !(it.quote_summary?.accepted > 0);
          const onCardClick = () => onOpen(it, awaitingQuotes ? "quotes-expand" : "details");
          const startsLate = startsAfterDeadline(it);
          const supplierName = it.assigned_supplier?.supplier_name || it.external_supplier?.supplier_name;
          return (
            <div
              key={it.id}
              className={`rounded-lg border p-3 cursor-pointer transition-colors ${startsLate ? "border-amber-400 bg-amber-50/60 hover:border-amber-500" : "border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50"}${it.deleted_at ? " opacity-60 [&_*]:line-through" : ""}`}
              data-testid={`maint-card-${it.id}`}
              role="button"
              tabIndex={0}
              onClick={onCardClick}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCardClick(); } }}
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  {assignee ? (
                    <div className="text-[11px] text-zinc-500 truncate" data-testid={`maint-card-assignee-${it.id}`}>
                      {supplierName && supplierName !== (assignee.name || assignee.email) && <span>{supplierName} · </span>}
                      {assignee.name || assignee.email}
                      <span className="text-zinc-400"> · {displayRole(assignee.role)}</span>
                    </div>
                  ) : supplierName ? (
                    <div className="text-[11px] text-zinc-500 truncate" data-testid={`maint-card-external-${it.id}`}>
                      {supplierName}
                      {it.assigned_supplier?.contact_person && <span> · {it.assigned_supplier.contact_person}</span>}
                      <span className="text-zinc-400"> · {it.external_supplier?.supplier_name && !it.assigned_supplier ? "offline" : displayRole("provider")}</span>
                    </div>
                  ) : null}
                  <div className="font-medium text-sm break-words">{it.title}</div>
                  {it.description && (
                    <div className="text-xs text-zinc-500 line-clamp-2 break-words">{it.description}</div>
                  )}
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
                    {catsOf(it).map((c) => (
                      <span key={c} className="inline-flex rounded-full border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-medium text-zinc-700">{c}</span>
                    ))}
                    <PriorityPill v={it.priority} />
                    {(it.due_date || "").slice(0, 10) && (
                      <span>deadline {(it.due_date || "").slice(0, 10)}</span>
                    )}
                    {startsLate && <LateStartChip it={it} />}
                  </div>
                  {(it.quote_summary || it.unread_messages > 0) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <QuoteSummaryBadge summary={it.quote_summary} onClick={(e) => { e.stopPropagation(); onOpen(it, "quotes-expand"); }} />
                      <MessagesBadge count={it.unread_messages} onClick={(e) => { e.stopPropagation(); onOpen(it, "quotes-expand"); }} />
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  {it.cost != null && (
                    <div className="text-sm font-semibold font-mono" data-testid={`maint-card-cost-${it.id}`}>
                      {it.assigned_supplier?.currency ? `${it.assigned_supplier.currency} ` : ""}{it.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  )}
                  <div className={it.cost != null ? "mt-1" : ""}><StatusBadge value={it.status} /></div>
                  {it.cancel_request && it.status !== "cancelled" && (
                    <div className="mt-1 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700" data-testid={`cancel-pending-chip-${it.id}`}>
                      Cancellation pending
                    </div>
                  )}
                  {it.status === "dispute" && (it.rework_deadline || "").slice(0, 10) && (it.rework_deadline || "").slice(0, 10) < new Date().toISOString().slice(0, 10) && (
                    <div className="mt-1 text-[11px] font-semibold text-red-600" data-testid={`rework-overdue-chip-${it.id}`}>Rework overdue</div>
                  )}
                  {["confirmed", "full_payment_confirmed"].includes(it.status) && !it.deleted_at && (
                    <div className="mt-1.5">
                      <ShowInvoiceButton invoiceId={it.invoice_id} testid={`show-invoice-btn-${it.id}`} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
